// Sérialisation pure de l'état calendrier ↔ URLSearchParams (EPIC E PR1).
import type { CalendarEventKind, CalendarFilters } from './calendarAggregate';

export type CalendarView = 'month' | 'week' | 'day' | 'list' | 'planning';

export interface CalendarUrlState {
  view: CalendarView;
  date: string; // YYYY-MM-DD
  filters: CalendarFilters;
}

/** Sentinelle stable pour les tests ; remplacée par la vraie date au runtime. */
export const DEFAULT_DATE_FALLBACK = '__TODAY__';

const VALID_VIEWS: CalendarView[] = ['month', 'week', 'day', 'list', 'planning'];
const VALID_KINDS: CalendarEventKind[] = ['hearing', 'task', 'prescription', 'appointment'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function csv(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

const MENU_VIEWS: CalendarView[] = ['day', 'week', 'month', 'planning'];

/** Vue d’adresse valide, ou null. `workweek` compte comme la semaine. */
function viewFromUrl(raw: string | null): CalendarView | null {
  if (!raw) return null;
  const view = raw === 'workweek' ? 'week' : raw;
  return (VALID_VIEWS as string[]).includes(view) ? (view as CalendarView) : null;
}

/**
 * Une vue déjà dans l’adresse gagne, y compris Mois.
 * Sinon la dernière vue du sélecteur (Journée, Semaine, Mois, Vue Planning).
 * `list` en souvenir ouvre la Vue Planning. Sans rien : Mois.
 */
export function resolveCalendarView(rawUrlView: string | null, stored: string | null): CalendarView {
  const fromUrl = viewFromUrl(rawUrlView);
  if (fromUrl) return fromUrl;
  if (stored === 'list') return 'planning';
  if (stored && (MENU_VIEWS as string[]).includes(stored)) return stored as CalendarView;
  return 'month';
}

export function parseCalendarParams(sp: URLSearchParams): CalendarUrlState {
  const rawView = sp.get('view') === 'workweek' ? 'week' : sp.get('view');
  const view: CalendarView =
    rawView && (VALID_VIEWS as string[]).includes(rawView)
      ? (rawView as CalendarView)
      : 'month';

  const rawDate = sp.get('date');
  const date = rawDate && ISO_DATE.test(rawDate) ? rawDate : DEFAULT_DATE_FALLBACK;

  const lawyerIds = csv(sp.get('avocat'));
  const dossierId = sp.get('dossier') || null;
  const kinds = csv(sp.get('type')).filter(
    (k): k is CalendarEventKind => (VALID_KINDS as string[]).includes(k),
  );

  return { view, date, filters: { lawyerIds, dossierId, kinds } };
}

/**
 * Date → `YYYY-MM-DD` à partir des composantes **locales** (jamais
 * `toISOString()`, qui décale d'un jour pour les fuseaux à l'est d'UTC et
 * provoquait une boucle de remount du calendrier — cf. EPIC E PR1 hotfix).
 */
export function fcDateToYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toSearchParams(state: CalendarUrlState): URLSearchParams {
  const sp = new URLSearchParams();
  if (state.view !== 'month') sp.set('view', state.view);
  if (state.date && state.date !== DEFAULT_DATE_FALLBACK) sp.set('date', state.date);
  if (state.filters.lawyerIds.length > 0) sp.set('avocat', state.filters.lawyerIds.join(','));
  if (state.filters.dossierId) sp.set('dossier', state.filters.dossierId);
  if (state.filters.kinds.length > 0) sp.set('type', state.filters.kinds.join(','));
  return sp;
}
